<template>
	<div
		id="view-mode"
		class="app view-mode"
		:class="{ mobile: isMobileWebApp, landscape: isLandscape, portrait: isMobilePortrait }"
	>
		<Layout>
			<template #media>
				<EventEmbedPreview
					v-if="showEmbedLoginWallOverlay || showEmbedPaymentOverlay || showEventEmbedPreview"
					class="event-embed-preview"
					:event="event"
					:force-login-button="showEmbedLoginWallOverlay"
					:force-payment-button="showEmbedPaymentOverlay"
					:show-infos="eventCardInfosEnabled"
					@init-embed-player="initEmbedPlayer"
				/>
				<div
					v-else-if="isLoadingLiveStream"
					class="live-stream-loading"
				>
					<LoadingSpinner
						:delay="0"
						center
					/>
				</div>
				<ErrorScreen
					v-else-if="hasErrorScreen && !isLoopBackgroundMode"
					:info="errorScreenInformation"
					:screen="errorScreen"
				/>
				<PlayerWrapper
					v-else-if="!hasPlayoutError"
					ref="playerWrapper"
					:autoplay="autoplayEnabled"
					:force-ads-disabled="!canInitializeEmbedAds"
				/>
				<div
					v-else-if="isLoopBackgroundMode"
					class="view-mode__fallback-poster"
					:style="{ backgroundImage: `url(${eventPoster})` }"
				/>
				<EmbedGeoblockingOverlay v-if="isGeoblocked" />
			</template>
		</Layout>
		<!-- share -->
		<ShareModal
			v-if="showShare && !isUpcoming"
			class="custom-modals"
			:title="shareTitle"
			:event-title="eventTitle"
			:item="shareItem"
			:item-label="itemLabel"
			:links="shareLinks"
			:url="shareUrl"
			:url-label="urlLabel"
			:image-not-found-label="imageNotFoundLabel"
			:is-event="true"
			@close="handleShareClose"
		/>
		<!-- context menu mobile -->
		<ContextMenuMobile
			v-if="isMobileWebApp && !isUpcoming"
			class="custom-modals"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import playerTypes from '@videotools/js/constants/playerTypes';
import { MAX_INTERVAL } from '@videotools/js/constants/app';
import { bus, disableHeartbeat, enableHeartbeat, EVENT } from '@videotools/js/events';
import { events as HOTKEY_EVENT, processHotkeys } from '@videotools/js/events/modules/player-hotkeys';
import { getDateTime } from '@shared/utils/timeUtils';
import Layout from '@videotools/js/view-mode/components/Layout';
import EventEmbedPreview from '@components/EventEmbedPreview';
import LoadingSpinner from '@components/loader/LoadingSpinner';
import ShareModal from '@components/share-modal';
import { ContextMenuMobile } from '@components/context-menu';
import ErrorScreen from '@components/ErrorScreen';
import PlayerWrapper from '@components/player/PlayerWrapper';
import { getLocation } from '@shared/utils/eventUtils';
import EmbedGeoblockingOverlay from '@components/player/overlays/EmbedGeoblockingOverlay.vue';
import { ERROR } from '@videotools/js/logic/error-screen/ErrorScreen';
import {
	getMzCookieConsentMode,
	MZ_COOKIE_CONSENT_MODE,
	waitForMzCookieConsent,
} from '@videotools/js/util/waitForMzCookieConsent';

const SHARE_TYPE = {
	EVENT: 'event',
	TAG:   'tag',
	CLIP:  'clip',
};

const events = [EVENT.SHARE_TAG, EVENT.SHARE_CLIP, EVENT.SHARE_EVENT, HOTKEY_EVENT.HOTKEY_TV_BACK];
const LIVE_STREAM_RETRY_DELAY = 5000;

export default {
	name:       'ViewMode',
	components: {
		Layout,
		EventEmbedPreview,
		LoadingSpinner,
		ShareModal,
		ContextMenuMobile,
		ErrorScreen,
		PlayerWrapper,
		EmbedGeoblockingOverlay,
	},
	props: {
		playerType: {
			type:      [String, Object],
			default:   () => null,
			validator: (type) => Object.values(playerTypes).includes(type) || type === null,
		},
	},
	data() {
		const isWaitingForMzEmbedConsentDecision =
			getMzCookieConsentMode() === MZ_COOKIE_CONSENT_MODE.PARENT_CMP;

		return {
			// share
			showShare:                false,
			shareItem:                null,
			shareType:                null,
			isUpcoming:               true,
			isLoadingLiveStream:      false,
			isComponentDestroyed:     false,
			eventTimer:               null,
			// player container height sync
			initEmbedPlayerRequested: false,
			isWaitingForMzEmbedConsentDecision,
			canInitializeEmbedAds:    !isWaitingForMzEmbedConsentDecision,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:        'app/isMobileWebApp',
			isLandscape:           'device/isLandscape',
			isMobilePortrait:      'device/isMobilePortrait',
			supportsNativeShare:   'device/supportsNativeShare',
			isTouchDevice:         'device/isTouchDevice',
			event:                 'event/event',
			eventURL:              'event/eventURL',
			eventTitle:            'event/eventTitle',
			streamURL:             'event/streamURL',
			isGeoblocked:          'event/isGeoblocked',
			isPaymentProtected:    'event/isPaymentProtected',
			errorScreen:           'error/errorScreen',
			hasErrorScreen:        'error/hasErrorScreen',
			hasPlayoutError:       'error/hasPlayoutError',
			isEmbedded:            'features/isEmbedded',
			autoplay:              'features/autoplay',
			isLoopBackgroundMode:  'features/isLoopBackgroundMode',
			eventCardInfosEnabled: 'features/eventCardInfosEnabled',
		}),
		shareTitle() {
			switch (this.shareType) {
			case SHARE_TYPE.EVENT:
				return this.shareLabels.SHARE_EVENT;
			case SHARE_TYPE.TAG:
				return this.shareLabels.SHARE_TAG;
			case SHARE_TYPE.CLIP:
				return this.shareLabels.SHARE_CLIP;
			default:
				return this.shareLabels.SHARE;
			}
		},
		itemLabel() {
			const parentCategory = this.$Helpers.ucfirst(this.$I18n.trans('event.category.parent')[this.event.categoryParent] ?? '');
			const childCategory = this.$Helpers.ucfirst(this.$I18n.trans('event.category.child')[this.event.categoryChild] ?? '');
			return `${parentCategory} • ${childCategory}`;
		},
		shareUrl() {
			if (!this.shareItem) {
				return '';
			}
			return this.shareItem.url;
		},
		shareLinks() {
			return this.shareItem?.shareLinks ?? { facebook: '', twitter: '', mail: '' };
		},
		urlLabel() {
			const labels = {
				[SHARE_TYPE.TAG]:       this.shareLabels.TAG_URL,
				[SHARE_TYPE.CLIP]:      this.shareLabels.CLIP_URL,
				[SHARE_TYPE.COPY_LINK]: this.shareLabels.COPY_LINK,
			};
			return labels[this.shareType] || this.shareLabels.COPY_LINK;
		},
		imageNotFoundLabel() {
			return this.shareLabels.PREVIEW_NOT_FOUND;
		},
		errorScreenInformation() {
			const parentCategory = this.$Helpers.ucfirst(this.$I18n.trans('event.category.parent')[this.event.categoryParent] ?? '');
			const childCategory = this.$Helpers.ucfirst(this.$I18n.trans('event.category.child')[this.event.categoryChild] ?? '');

			return {
				name:          this.event.name || this.$I18n.trans('commons.unnamed'),
				eventContext:  this.event.contextable && this.event.contextable.name,
				parentCategory,
				childCategory,
				eventDateTime: getDateTime(this.event.startTimestamp + (this.event.kickoffOffsetMinutes * 60)),
				location:      getLocation(this.event),
				url:           this.eventURL,
			};
		},
		showEventEmbedPreview() {
			if (this.isWaitingForMzEmbedConsentDecision) return true;

			if (this.initEmbedPlayerRequested || this.isLoopBackgroundMode || (this.autoplay && !this.isUpcoming)) {
				return false;
			}
			let isExternalEmbed = false;
			try {
				Boolean(parent.document.body);
			} catch {
				isExternalEmbed = true;
			}

			// upcoming events don't have a player, so we show the event card
			// on external embeds we want to save some bandwith, hence we show first the event card
			return this.isUpcoming || isExternalEmbed;
		},
		autoplayEnabled() {
			return this.autoplay ? true : this.initEmbedPlayerRequested;
		},
		showEmbedLoginWallOverlay() {
			return this.hasErrorScreen &&
				!this.isLoopBackgroundMode &&
				this.errorScreen?.errorKey === ERROR.EVENT_AUTH_REQUIRED;
		},
		showEmbedPaymentOverlay() {
			return this.hasErrorScreen &&
				!this.isLoopBackgroundMode &&
				this.errorScreen?.errorKey === ERROR.EVENT_PAYMENT_REQUIRED;
		},
		eventPoster() {
			return this.event.imageUrl;
		},
	},
	watch: {
		'event.startTimestamp'() {
			this.scheduleLiveEventTransition();
		},
	},
	created() {
		for (const event of events) {
			bus.subscribe(event, this[`BUS-${event}`]);
		}

		if (this.playerType) {
			this.setPlayerType(this.playerType);
		}

		if (window.arenaPlayerEmbedConfig?.heartbeatEnabled !== false) {
			enableHeartbeat();
		}
	},
	mounted() {
		this.waitForMzEmbedConsentDecision();
		this.scheduleLiveEventTransition();

		// Register hotkeys
		if (!this.isMobileWebApp) {
			this.registerHotkeys();
		}

		// listen for play message from the parent
		window.addEventListener('message', (event) => {
			if (event.data.command === 'play') {
				this.initEmbedPlayer();
			}
		});
	},
	destroyed() {
		this.isComponentDestroyed = true;
		clearTimeout(this.eventTimer);
		disableHeartbeat();

		for (const event of events) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}

		// Hotkeys
		if (!this.isMobileWebApp) {
			document.removeEventListener('keydown', this.processHotkeys);

			// Outside the iframe
			try {
				if (typeof parent !== 'undefined' && document !== parent?.document) {
					parent.document.removeEventListener('keydown', this.processHotkeys);
				}
			} catch {}
		}
	},
	methods: {
		...mapActions({
			setPlayerType:   'app/setPlayerType',
			shareNative:     'device/shareNative',
			loadEventStream: 'event/loadEventStreamPlayout',
		}),
		[`BUS-${EVENT.SHARE_TAG}`](tag) {
			this.shareItem = tag;
			this.shareType = SHARE_TYPE.TAG;
			this.showShare = true;
			this.shareNativeOrOpenShareModal({
				url: this.shareUrl,
			});
		},
		[`BUS-${EVENT.SHARE_CLIP}`](tag) {
			this.shareItem = tag;
			this.shareType = SHARE_TYPE.CLIP;
			this.showShare = true;
			this.shareNativeOrOpenShareModal({
				url: this.shareUrl,
			});
		},
		[`BUS-${EVENT.SHARE_EVENT}`]() {
			this.shareItem = this.event;
			this.shareType = SHARE_TYPE.EVENT;
			this.showShare = true;
			this.shareNativeOrOpenShareModal({
				url:  this.shareUrl,
				text: this.eventTitle,
			});
		},
		shareNativeOrOpenShareModal(data) {
			if (this.isTouchDevice && this.supportsNativeShare) {
				this.shareNative(data);
			} else {
				this.showShare = true;
			}
		},
		[`BUS-${HOTKEY_EVENT.HOTKEY_TV_BACK}`]() {
			if (this.showEventEmbedPreview || this.hasErrorScreen) {
				window.parent.postMessage({ command: 'navigate:Back' }, '*');
			} else {
				const playerWrapper = this.$refs.playerWrapper;
				if (playerWrapper) {
					playerWrapper.handleTVBack();
				}
			}
		},
		registerHotkeys() {
			document.addEventListener('keydown', this.processHotkeys);
			// Outside the iframe
			try {
				if (typeof parent !== 'undefined' && document !== parent?.document) {
					parent.document.addEventListener('keydown', this.processHotkeys);
				}
			} catch {}
		},
		processHotkeys(event) {
			processHotkeys(event, 'NAV_BACK', false);
		},
		handleShareClose() {
			this.showShare = false;
		},
		// Only timer-fired calls fetch playout because app initialization already handled events that started before mount.
		scheduleLiveEventTransition(handleStartedEvent = false) {
			clearTimeout(this.eventTimer);

			const millisecondsUntilLive = (Number(this.event?.startTimestamp) * 1000) - Date.now();
			if (!Number.isFinite(millisecondsUntilLive)) {
				this.isUpcoming = false;
				return;
			}

			this.isUpcoming = millisecondsUntilLive > 0;
			if (this.isUpcoming) {
				this.eventTimer = setTimeout(
					() => this.scheduleLiveEventTransition(true),
					Math.min(millisecondsUntilLive, MAX_INTERVAL),
				);
				return;
			}

			if (handleStartedEvent) {
				this.handleLiveEventStarted();
			}
		},
		async handleLiveEventStarted() {
			this.isUpcoming = false;

			if (!this.isLoadingLiveStream) {
				// Pre-live playout URLs can contain short-lived CDN tokens, so refresh them at the scheduled start.
				await this.loadLiveEventStream(true);

				// A stream can become available shortly after the scheduled start; retry the transient failure once.
				if (!this.isComponentDestroyed && this.errorScreen?.errorKey === ERROR.EVENT_PLAYOUT_FAILED) {
					this.eventTimer = setTimeout(() => {
						if (!this.isComponentDestroyed) {
							this.loadLiveEventStream(true);
						}
					}, LIVE_STREAM_RETRY_DELAY);
				}
			}
		},
		// Fetch playout while keeping the existing loading and error-screen transitions intact.
		async loadLiveEventStream(forceRefresh = false) {
			if (this.isComponentDestroyed || (!forceRefresh && this.streamURL) || this.isLoadingLiveStream) return;

			this.isLoadingLiveStream = true;

			try {
				await this.loadEventStream(this.event.id);
			} finally {
				this.isLoadingLiveStream = false;
			}
		},
		async initEmbedPlayer() {
			if (this.isComponentDestroyed || this.initEmbedPlayerRequested) return;

			// Mount only after preparation; mounting before an await can leave an orphan autoplaying player.
			await this.waitForMzEmbedConsentDecision();
			if (this.isComponentDestroyed) return;

			await this.loadLiveEventStream(true);
			if (this.isComponentDestroyed) return;

			this.initEmbedPlayerRequested = true;
		},
		async waitForMzEmbedConsentDecision() {
			if (!this.isWaitingForMzEmbedConsentDecision) return;

			const consentDecisionWasReceived = await waitForMzCookieConsent();
			if (this.isComponentDestroyed) return;

			this.canInitializeEmbedAds = consentDecisionWasReceived;
			this.isWaitingForMzEmbedConsentDecision = false;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.app {
	width: 100%;
	height: 100%;
	background: var(--color-black);
	color: var(--color-soft-white);

	body.-t-light & {
		color: var(--color-black);
	}

	/* sdx overrides */
	.table table {
		background: var(--color-black);
		color: var(--color-soft-white);
	}

	.event-embed-preview {
		height: 100%;
		width: 100%;
	}

	.live-stream-loading {
		width: 100%;
		aspect-ratio: 16 / 9;
		background-color: var(--color-black);
	}
}

.app > div:not(.custom-modals) {
	height: 100%;
}

.custom-modals {
	position: absolute;
	width: 100%;
	bottom: 0;
	left: 0;
	height: auto;
	z-index: 999;
}

.view-mode__fallback-poster {
	background-size: cover;
	background-position: center;
	height: 100%;
	width: 100%;
}
</style>
